import { Injectable } from "@angular/core";
import { Contract } from "@edp-components/edp-contract";
import { HasPackModel } from "@edp-components/edp-contract/edp-contract.model";
import { environment } from "@edp-environments/environment";
import { ContractEs } from "@edp-models/ES/es-contract.model";
import { Menu, MenuFactory, MenuList, MenuResponse } from "@edp-models/menu.model";
import { EdpUser } from "@edp-models/user.model";
import { ApiService } from "@edp-services/api.service";
import { RefreshMainMenu, SetTabMenu } from "@edp-store/actions/layout.actions";
import { getContractEs } from "@edp-store/selectors/ES/contract-es.selectors";
import { getContract } from "@edp-store/selectors/contract.selectors";
import { getActiveTabMenu } from "@edp-store/selectors/layout.selectors";
import { getUser } from "@edp-store/selectors/user.selectors";
import { IAppEsState } from "@edp-store/state/ES/app-es.state";
import { IAppState } from "@edp-store/state/app.state";
import { select, Store } from "@ngrx/store";
import { of } from "rxjs";
import { filter, switchMap } from "rxjs/operators";

@Injectable({
    providedIn: "root",
})
export class MenuService {
    private contract: Contract;
    private contractES: ContractEs;
    private user: EdpUser;

    private menu: MenuResponse[];

    constructor(
        private readonly store: Store<IAppState>,
        private readonly storeES: Store<IAppEsState>,
        private readonly apiService: ApiService
    ) {
        this.store.select(getUser).subscribe(user => (this.user = user));
        this.store.select(getContract).subscribe(contract => (this.contract = contract));
        this.storeES.select(getContractEs).subscribe(contract => (this.contractES = contract));
        this.store
            .pipe(
                select(getActiveTabMenu),
                filter(tab => !!tab)
            )
            .subscribe(() => {
                this.store.dispatch(new RefreshMainMenu());
            });
    }

    public getMenu() {
        return this.menu?.length
            ? of(this.menu)
            : this.apiService.callServicePostToCollection(`configuration.getMenu`, {}).pipe(
                  switchMap((menu: MenuList) => {
                      this.menu = this.getMenuList(menu);
                      return of(this.menu);
                  })
              );
    }

    public convertMenu(menuResponse: MenuResponse[]): Menu[] {
        const paramRegex = new RegExp(/:.\w+/);
        const paramList: { [key: string]: string } = {
            ":contract_id": !!this.contract?.ContractId ? this.contract?.ContractId : !!this.contractES?.id ? this.contractES?.id : "",
            ":nickname": this.contract?.Nickname,
        };

        return this.parseMenu(menuResponse, paramRegex, paramList);
    }

    public hasFunciona(): boolean {
        return (this.contract?.HasPack instanceof HasPackModel ||
            this.contract?.HasServiceFunciona ||
            this.contract?.HasServiceFuncionaCA);
    }

    public hasPack(): boolean {
        return (this.contract?.HasPack ? true : false) ?? false;
    }

    public hasPackFull(): boolean {
        return (this.contract?.HasPack ? this.contract?.HasPack.full : false) ?? false;
    }

    public hasHealth(): boolean {
        return (this.contract?.HasPack ? this.contract?.HasPack.full : false)
        || (this.contract?.HasHealth ? this.contract?.HasHealth : false);
    }

    public dontHaveGas(): boolean {
        return !this.contract?.HasG ?? false;
    }

    public hasPartnerNumber(): boolean {
        return (this.user?.partnerNumber ? true : false) ?? false;
    }

    private getMenuList(menu: MenuList): MenuResponse[] {
        let menuList = menu.desktop;

        if (environment.isApp) {
            menuList = menu.app;
        }

        return menuList;
    }

    private parseMenu(menuResponse: MenuResponse[], paramRegex: RegExp, paramList: { [key: string]: string }): Menu[] {
        return menuResponse.map(menu => {
            const menuItem = MenuFactory.create(menu);

            const routeParams = paramRegex.exec(menu.routerPath);
            if (routeParams && routeParams.length && menu.routerPath) {
                let routerPath = menu.routerPath;
                routeParams.forEach(routeParam => {
                    routerPath = routerPath.replace(routeParam, paramList[routeParam] ?? "");
                });

                menuItem.routerPath = [routerPath];
            }
            if (menu.visibleCondition && menu.visible) {
                try {
                    menuItem.visible = this[menu.visibleCondition]();
                } catch (error) {
                    menuItem.visible = false;
                    console.error("error", error);
                    console.error("menuItem", menuItem);
                }
            }
            if (menuItem.name && menuItem.name.indexOf(":") >= 0) {
                const nameParams = paramRegex.exec(menuItem.name);

                if (nameParams) {
                    nameParams.forEach(routeParam => {
                        menuItem.name = menuItem.name.replace(routeParam, paramList[routeParam] ?? "");
                    });
                }
            }

            if (menuItem.tab) {
                menuItem.callToAction = () => {
                    this.store.dispatch(new SetTabMenu(menuItem.tab));
                };
            }

            menuItem.submenu = this.parseMenu(menu.submenu, paramRegex, paramList);

            return menuItem;
        });
    }
}
